import React from "react";
import { overlayBase, useParticles } from "./_shared";

interface Props {
  /** Number of bokeh circles, default 14 */
  count?: number;
  /** Fill colour (rgba), default semi-transparent white */
  color?: string;
  /** Minimum circle diameter px, default 6 */
  minSize?: number;
  /** Maximum circle diameter px, default 22 */
  maxSize?: number;
  /** Base animation duration seconds, default 8 */
  dur?: number;
}

/**
 * BokehDust — soft blurred floating circles drifting slowly upward.
 */
const BokehDust: React.FC<Props> = ({
  count = 14,
  color = "rgba(255,255,255,0.35)",
  minSize = 6,
  maxSize = 22,
  dur = 8,
}) => {
  const parts = useParticles(
    count,
    (i, r) => ({
      id: i,
      left: r(0) * 100,
      top: 10 + r(1) * 80,
      size: minSize + r(2) * (maxSize - minSize),
      delay: r(3) * dur,
      drift: (r(4) - 0.5) * 40,
      d: dur * (0.7 + r(5) * 0.6),
    }),
    [count, minSize, maxSize, dur]
  );

  return (
    <div style={overlayBase}>
      <style>{`
        @keyframes oot-bokeh {
          0%   { transform: translate(0,0) scale(0.8); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translate(var(--dx), -40px) scale(1.15); opacity: 0; }
        }
      `}</style>
      {parts.map((p) => (
        <div
          key={p.id}
          style={{
            position: "absolute",
            left: `${p.left}%`,
            top: `${p.top}%`,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: color,
            filter: "blur(2px)",
            animation: `oot-bokeh ${p.d}s ease-in-out ${p.delay}s infinite`,
            "--dx": `${p.drift}px`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
};

export default BokehDust;
